import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearOrders,
  setTourParameter,
  setTerritoryParameter,
  setUserParam,
  saveUserSettings,
  setSolutionJson,
} from 'actions';
import withTranslation from 'hoc/withTranslation';
import config from 'config';
import { find, isEqual, map, isEmpty } from 'lodash';
import styled from '@emotion/styled';
import TerritoryPlanningContainer from 'components/FloatingPanels/TerritoryPlanning/TerritoryPlanningContainer';
import { PANELS } from 'reducers/user';
import moment from 'moment/moment';
import Checkbox from '../../Form/Checkbox';
import TimeRangeSlider from './TimeRangeSlider';
import { isMobileDevice, isProdEnv } from '../../../utils/helpers';
import {
  StyledLine,
  StyledTextInput,
  StyledWizardContent,
  StyledWizardH1,
  StyledSelectBigInput,
  StyledIcon,
  StyledSelectedInputLabel,
  StyledInformationNormalIcon,
} from '../Global/WizardStyled';
import InputLocation from '../Global/InputLocation';
import Section from '../../Form/Section';
import Label from '../../Form/Label';
import Selector from '../../Form/Selector';
import iconEdit from '../../../global/img/edit-dark.svg';
import iconDelete from '../../../global/img/delete.svg';
import { AmplitudeService, AMPLITUDE_EVENTS } from '../../../utils/amplitude';
import { getFleetTypeIcon } from '../../../utils/FleetHelpers';
import FleetEndAddress from './FleetEndAddress';
import FleetManagerModal from './FleetManagerModal';
import { APP_MODES, getAppMode } from '../../../utils/urlHelpers';
import { getSafeValue } from '../../../utils/security';
import { createRandomTerritories } from '../../../utils/territories/TerritoriesHelpers';
import { getAtLocation } from '../../../utils/GeoCoder';
import { getBoundingCoordinates } from '../../../utils/MapHelpers';
import { colors, fontSize } from '../../../global/variables';

const appMode = getAppMode();
const isProd = isProdEnv();
const isEVFF = appMode.includes(APP_MODES.EV_PROFILES);

const { secondaryTextColor, primaryTextColor, aqua, primaryBorderColor, secondaryBorderColor } =
  colors;
const { small } = fontSize;

const isMobile = isMobileDevice();
const {
  scenario: { times: timesConfig },
  maxValues,
  shortTimeFormat,
} = config;

const StyledFleetTypeSectionHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  marginBottom: '0.5rem',
});

const StyledFleetTypeCountBadge = styled.span({
  fontSize: small,
  fontWeight: 700,
  backgroundColor: 'rgba(0,0,0,0.03)',
  border: `1px solid ${secondaryBorderColor}`,
  borderRadius: '1.5rem',
  padding: '0.05rem 0.45rem',
  color: secondaryTextColor,
});

const StyledFleetTypeRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0.6rem',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  marginBottom: '0.6rem',
  boxSizing: 'border-box',
});

const StyledFleetTypeNameLabel = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  fontWeight: 700,
  color: primaryTextColor,
  width: '4.5rem',
  flexShrink: 0,
  overflow: 'hidden',
});

const StyledFleetTypeNameText = styled.span({
  display: 'inline-block',
  flex: '1 1 auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
});

const StyledFleetTypeNameIcon = styled.div(({ iconUrl }) => ({
  width: '1.2rem',
  height: '1.2rem',
  flexShrink: 0,
  backgroundImage: `url(${iconUrl})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
}));

const StyledFleetTypeSelect = styled(StyledSelectBigInput)({
  flex: '1 1 auto',
  minWidth: '4rem',
  width: 'auto',
});

const StyledFleetTypeAmountInput = styled(StyledTextInput)({
  width: '3.6rem',
  flexShrink: 0,
  textAlign: 'center',
  padding: '0.55rem 0.3rem',
});

const StyledFleetTypeUnitsLabel = styled.span({
  fontSize: small,
  color: secondaryTextColor,
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

const StyledFleetTypeActionIcon = styled(StyledIcon)(({ variant, disabled }) => ({
  cursor: disabled ? 'not-allowed' : 'pointer',
  width: '1.6rem',
  height: '1.6rem',
  backgroundSize: '0.9rem',
  backgroundPosition: 'center',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  borderRadius: '0.12rem',
  flexShrink: 0,
  opacity: disabled ? 0.4 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
  border: `1px solid ${variant === 'delete' ? 'rgba(255,102,102,.5)' : aqua}`,
  ':hover': {
    backgroundColor: disabled
      ? 'transparent'
      : variant === 'delete'
      ? 'rgba(255,102,102,.12)'
      : 'rgba(34,205,200,.12)',
  },
}));

const StyledAddFleetTypeButton = styled.button({
  width: '100%',
  background: 'transparent',
  color: aqua,
  border: `1px solid ${aqua}`,
  borderRadius: '1.5rem',
  padding: '0.5rem',
  fontSize: small,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontFamily: 'FiraGO',
  cursor: 'pointer',
  marginTop: '0.2rem',
  marginBottom: '0.6rem',
  transition: 'all 250ms cubic-bezier(0.365, 0.005, 0.285, 1.005)',
  ':hover': {
    backgroundColor: 'rgba(34,205,200,.08)',
  },
});

const StyledTerritoryPanel = styled.div`
  display: grid;
  grid-template-columns: fit-content(50%) fit-content(10%) fit-content(40%);
  justify-content: start;
  grid-gap: 2rem;
`;

const StyledDropText = styled.span({
  fontWeight: '700',
  height: '1.75rem',
  fontSize: small,
  color: secondaryTextColor,
  marginRight: '0.5rem',
  span: {
    color: aqua,
    ':hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
});

let hasRestoredFromLocalStorage = false;

const Step1ContainerMultiFleet = ({
  user,
  oAuth,
  tourPlanner,
  mapData,
  onTerritoryPlanningPanelToggle,
  solution,
  translations: {
    error: { depotNotFound },
    global: { timesTrans },
    locationPopup: { fleetTrans, infoEnableSettings, fleetEnabledTrans, planEnabledTrans },

    map: { depotMarkerTrans },
    tourPlanner: {
      depotLabelTrans,
      depotPlaceholderTrans,
      configurationLabelTrans,
      timeLabelTrans,
    },
    territoryPlanning: {
      territoryLabelTrans,
      territorySwitchLabel,
      territoryPlanningInformation,
      territoryStrictSwitchLabel,
    },
    wizard: {
      fleet: {
        vehiclesAmountLabel,
        vehiclesAmountLabelMobile,
        fleetTypeTitle,
        editFleetTypeTooltip,
        deleteFleetTypeTooltip,
        unitsLabel,
        addFleetTypeLabel,
      },
    },
  },
}) => {
  const currentStep = useSelector((state) => state.usageContext.currentStep);
  const territories = useSelector(({ areas: stateAreas }) => stateAreas.territories);
  const dispatch = useDispatch();
  const handleClearOrders = useCallback((data) => dispatch(clearOrders(data)), [dispatch]);
  const handleSetTourParameter = useCallback(
    (parameter) =>
      dispatch(setTourParameter({ ...parameter, index: solution.show, userChange: true })),
    [dispatch, solution.show],
  );
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);

  const handleSetTerritoryParameter = useCallback(
    (parameter) => dispatch(setTerritoryParameter(parameter)),
    [dispatch],
  );

  const {
    time: { key: timeTourParamKey, value: timeTourParamValue },
    vehicles,
    location,
    vehicleProfiles,
  } = tourPlanner || {};

  const [times, setTimes] = useState(timeTourParamValue);
  const [noDepotFound, setNoDepotFound] = useState(false);
  const [showTerritoryPlanning, setShowTerritoryPlanning] = useState(false);
  const [fleetManagerConfig, setFleetManagerConfig] = useState(null);
  const [tpObj] = useState(() => {
    const savedData = localStorage.getItem('userSettings');
    return savedData ? JSON.parse(savedData) : tourPlanner;
  });
  const fleetEnabled = user.fleetEnabled;
  const planEnabled = user.planEnabled;
  const lastImport = user.lastImport;
  const csvOrders = lastImport?.filename?.endsWith('.csv') && !isEmpty(lastImport.delimiter);

  const tpVersion = user?.tpaVersion?.version;
  const isDevOrSit = tpVersion === 'dev' || tpVersion === 'sit';
  const canFleetTypeBeElectric = (type) =>
    isEVFF && isDevOrSit && (type === 'taxi' || type === 'car');

  const cleanJSONFile = useCallback(() => {
    // tourPlanner.fileType is set to 'json' by the JSON viewer for ANY edit, even on manually
    // created (box-order) problems that were never actually imported from a JSON file (see
    // recalculateSolutionParams, which defaults fileExt to 'json' when there's no uploaded
    // filename). Also require ordersMode === 'json_import' so we only clear orders for problems
    // that genuinely originated from a JSON import, not manual/CSV orders merely viewed/edited
    // in the JSON viewer.
    if (tourPlanner.fileType === 'json' && tourPlanner.ordersMode === 'json_import' && !csvOrders)
      handleClearOrders({ index: solution.show });
  }, [tourPlanner, solution.show, csvOrders]);

  useEffect(() => cleanJSONFile(), []);

  useEffect(() => {
    onTerritoryPlanningPanelToggle(showTerritoryPlanning);
  }, [showTerritoryPlanning, onTerritoryPlanningPanelToggle]);

  const timeOptions = useMemo(
    () => map(timesConfig, ({ key }) => ({ key, value: getSafeValue(timesTrans, key) })),
    [timesTrans],
  );

  const onEditFleetType = useCallback(
    (index) => setFleetManagerConfig({ mode: 'editType', typeIndex: index }),
    [],
  );

  const onDeleteFleetType = useCallback(
    (index) => {
      if (tourPlanner.vehicles.length <= 1) return;
      const updatedVehicles = tourPlanner.vehicles.filter((_, i) => i !== index);
      handleSetTourParameter({ vehicles: updatedVehicles });
      dispatch(setSolutionJson({ jsonTriggerVRP: true }));
      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_VEHICLE_NUMBER);
    },
    [handleSetTourParameter, tourPlanner, dispatch],
  );

  const onDeleteProfile = useCallback(
    (index) => {
      if (tourPlanner.vehicleProfiles.length <= 1) return;
      const profileToDelete = tourPlanner.vehicleProfiles[index];
      const isProfileInUse = tourPlanner.vehicles.some(
        (vehicle) => vehicle.profile === profileToDelete?.name,
      );
      if (isProfileInUse) return;
      const updatedProfiles = tourPlanner.vehicleProfiles.filter((_, i) => i !== index);
      handleSetTourParameter({ vehicleProfiles: updatedProfiles });
      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_FLEET_TYPE);
    },
    [handleSetTourParameter, tourPlanner],
  );

  const onAddFleetType = useCallback(
    () => setFleetManagerConfig({ mode: 'addType', typeIndex: null }),
    [],
  );

  const onCloseFleetManager = useCallback(() => setFleetManagerConfig(null), []);

  const onSaveProfile = useCallback(
    (
      index,
      { name, fleetType: newFleetType, isElectricVehicle: newIsElectricVehicle, evProfile },
    ) => {
      const profiles = [...tourPlanner.vehicleProfiles];
      const previousName = index !== null ? profiles[index]?.name : null;
      const updatedProfile = {
        name,
        fleetType: newFleetType,
        isElectricVehicle: newIsElectricVehicle,
      };
      if (newIsElectricVehicle && evProfile) {
        updatedProfile.evProfile = evProfile;
      }
      if (index === null) {
        profiles.push(updatedProfile);
      } else {
        profiles[index] = updatedProfile;
      }
      const updatedVehicles =
        previousName && previousName !== name
          ? tourPlanner.vehicles.map((v) =>
              v.profile === previousName ? { ...v, profile: name } : v,
            )
          : tourPlanner.vehicles;
      handleSetTourParameter({ vehicleProfiles: profiles, vehicles: updatedVehicles });
      dispatch(setSolutionJson({ jsonTriggerVRP: true }));
      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_FLEET_TYPE, {
        fleetType: newFleetType,
      });
    },
    [handleSetTourParameter, tourPlanner, dispatch],
  );

  const onSaveType = useCallback(
    (index, { name, id, profile, amount: newAmount, capacity: newCapacity, costs: newCosts }) => {
      const updatedVehicles = [...tourPlanner.vehicles];
      const existingEnabled = index !== null ? tourPlanner.vehicles[index]?.enabled : undefined;
      const updatedVehicle = {
        name,
        id,
        profile,
        amount: newAmount,
        capacity: [newCapacity],
        costs: newCosts,
        ...(existingEnabled !== undefined && { enabled: existingEnabled }),
      };
      if (index === null) {
        updatedVehicles.push(updatedVehicle);
      } else {
        updatedVehicles[index] = updatedVehicle;
      }
      handleSetTourParameter({ vehicles: updatedVehicles });
      dispatch(setSolutionJson({ jsonTriggerVRP: true }));
      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_VEHICLE_NUMBER);
    },
    [handleSetTourParameter, tourPlanner, dispatch],
  );

  const onToggleType = useCallback(
    (index) => {
      const updatedVehicles = [...tourPlanner.vehicles];
      const current = updatedVehicles[index];
      updatedVehicles[index] = { ...current, enabled: current.enabled === false };
      handleSetTourParameter({ vehicles: updatedVehicles });
      dispatch(setSolutionJson({ jsonTriggerVRP: true }));
    },
    [handleSetTourParameter, tourPlanner, dispatch],
  );

  const onToggleAllTypes = useCallback(
    (enabled) => {
      const updatedVehicles = tourPlanner.vehicles.map((v) => ({ ...v, enabled }));
      handleSetTourParameter({ vehicles: updatedVehicles });
      dispatch(setSolutionJson({ jsonTriggerVRP: true }));
    },
    [handleSetTourParameter, tourPlanner, dispatch],
  );

  const onSliderChange = useCallback(
    ({ start, end }) => {
      const newTimes = { start, end };

      if (!isEqual(newTimes, times)) {
        const { key } = find(timesConfig, ({ value }) => isEqual(newTimes, value)) || { key: null };
        setTimes(newTimes);
        handleSetTourParameter({
          time: { key, value: newTimes },
          offset: moment.parseZone(start, shortTimeFormat).utcOffset(),
        });
        AmplitudeService.log(AMPLITUDE_EVENTS.TIME_CONFIG_CHANGED);
      }
    },
    [handleSetTourParameter, setTimes, times],
  );

  const onTimeChange = useCallback(
    (timeKey) => {
      const { value } = find(timesConfig, ({ key }) => timeKey === key) || {};
      if (value) {
        setTimes(value);
        handleSetTourParameter({ time: { key: timeKey, value } });
      }
      AmplitudeService.log(AMPLITUDE_EVENTS.TIME_CONFIG_CHANGED);
    },
    [handleSetTourParameter, setTimes],
  );

  const onRowProfileChange = useCallback(
    (index, e) => {
      const updatedVehicles = [...tourPlanner.vehicles];
      updatedVehicles[index] = { ...updatedVehicles[index], profile: e.target.value };
      handleSetTourParameter({ vehicles: updatedVehicles });
      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_FLEET_TYPE);
    },
    [handleSetTourParameter, tourPlanner],
  );

  const onRowAmountBlur = useCallback(
    (index, e) => {
      const value = parseInt(e.target.value, 10);
      if (!Number.isNaN(value) && value >= 1 && value <= maxValues.vehicles) {
        const updatedVehicles = [...tourPlanner.vehicles];
        updatedVehicles[index] = { ...updatedVehicles[index], amount: value };
        handleSetTourParameter({ vehicles: updatedVehicles });
      } else {
        e.target.value = tourPlanner.vehicles[index].amount;
      }
      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_VEHICLE_NUMBER);
    },
    [handleSetTourParameter, tourPlanner],
  );

  const onLocationFound = useCallback(
    (loc) => {
      setNoDepotFound(!loc.value && !isEmpty(loc.label));
      dispatch(setSolutionJson({ depotChange: true }));
      handleSetTourParameter({ location: { ...loc }, uploadedLocations: false });
      if (loc && loc.value) AmplitudeService.log(AMPLITUDE_EVENTS.DEPOT_SET);
    },
    [handleSetTourParameter, setNoDepotFound],
  );

  const handleOnTerritoriesEnabled = useCallback(
    (val) => {
      const loc = getAtLocation(user, tourPlanner);
      const bbox =
        mapData && mapData.geo && mapData.geo.features.length >= 2
          ? mapData.geo.bbox
          : getBoundingCoordinates(loc, 300);

      let territoryAreas;
      if (appMode.includes(APP_MODES.TERRA)) {
        setShowTerritoryPlanning(val);
      } else if (val) {
        territoryAreas = createRandomTerritories(bbox);
      }
      handleSetTerritoryParameter({ isEnabled: val, areaDetails: territoryAreas, bbox });
      handleSetTerritoryParameter({ strict: true });
    },
    [tourPlanner, user, mapData],
  );

  const onOpenClick = () => {
    handleSetUserParam({ openPanel: PANELS.SOLVER_CONFIG });
  };

  const handleOnCloseTerritoryPlanning = useCallback(
    (territoryPlanningState) => {
      setShowTerritoryPlanning(false);
      handleSetTerritoryParameter({ isEnabled: territoryPlanningState });
    },
    [setShowTerritoryPlanning],
  );

  const handleStrictChange = useCallback(
    (val) => {
      handleSetTerritoryParameter({ strict: val });
    },
    [handleSetTerritoryParameter],
  );

  useEffect(() => {
    dispatch(saveUserSettings(tourPlanner));
  }, [tourPlanner, dispatch]);

  useEffect(() => {
    if (!hasRestoredFromLocalStorage && tpObj && Object.keys(tpObj).length > 0) {
      handleSetTourParameter({
        ...tpObj,
        ordersMode: tourPlanner.ordersMode,
        solverConfiguration: tourPlanner.solverConfiguration,
        csvConfig: tourPlanner.csvConfig,
      });
      hasRestoredFromLocalStorage = true;
    }
  }, [tourPlanner.ordersMode, tourPlanner.solverConfiguration, tourPlanner.csvConfig]);
  useEffect(() => {
    hasRestoredFromLocalStorage = false;
  }, [currentStep]);

  const show = false;
  // only temporary due to adding limits for vehicles
  const showDriverSchedule = false;

  return (
    <StyledWizardContent>
      {showTerritoryPlanning ? (
        <TerritoryPlanningContainer
          onClose={handleOnCloseTerritoryPlanning}
          user={user}
          tourPlanner={tourPlanner}
          oAuth={oAuth}
        />
      ) : (
        <>
          <StyledWizardH1>{depotMarkerTrans}</StyledWizardH1>
          <Section>
            {!noDepotFound && <Label>{depotLabelTrans}</Label>}
            {noDepotFound && <Label error>{depotNotFound}</Label>}
            <InputLocation
              id="input-start"
              placeholder={depotPlaceholderTrans}
              value={location.label}
              oAuth={oAuth}
              onLocationFound={onLocationFound}
              user={user}
              tourPlanner={tourPlanner}
              hasCurrentLocationControl
            />
            <FleetEndAddress
              oAuth={oAuth}
              user={user}
              tourPlanner={tourPlanner}
              solution={solution}
            />
          </Section>
          <StyledLine />
          {show && appMode.includes(APP_MODES.WIP, APP_MODES.TERRA) && (
            <>
              <StyledWizardH1>{territoryLabelTrans}</StyledWizardH1>
              <Section>
                <StyledTerritoryPanel>
                  <Checkbox
                    id="service-area-enabler"
                    label={territorySwitchLabel}
                    value={territories.isEnabled}
                    onChange={handleOnTerritoriesEnabled}
                  />
                  <StyledIcon
                    id="information-btn"
                    icon={StyledInformationNormalIcon}
                    title={territoryPlanningInformation}
                    mini
                  />
                  {territories.isEnabled && (
                    <Checkbox
                      id="service-area-strict"
                      label={territoryStrictSwitchLabel}
                      value={territories.strict}
                      onChange={handleStrictChange}
                    />
                  )}
                </StyledTerritoryPanel>
              </Section>
              <StyledLine />
            </>
          )}
          <StyledWizardH1>{configurationLabelTrans}</StyledWizardH1>
          <StyledFleetTypeSectionHeader>
            <StyledSelectedInputLabel>{fleetTypeTitle}</StyledSelectedInputLabel>
            <StyledFleetTypeCountBadge id="label-fleet-type-count">
              {vehicles.filter((v) => v.enabled !== false).length}
            </StyledFleetTypeCountBadge>
          </StyledFleetTypeSectionHeader>
          {vehicles.filter((v) => v.enabled !== false).map((fleetVehicle) => {
            const index = vehicles.indexOf(fleetVehicle);
            const linkedProfile =
              vehicleProfiles.find(({ name }) => name === fleetVehicle.profile) || {};
            const isLastFleetType = vehicles.filter((v) => v.enabled !== false).length <= 1;
            return (
              <StyledFleetTypeRow key={fleetVehicle.id || index}>
                <StyledFleetTypeNameLabel title={fleetVehicle.name || fleetVehicle.id}>
                  <StyledFleetTypeNameIcon iconUrl={getFleetTypeIcon(linkedProfile.fleetType)} />
                  <StyledFleetTypeNameText>
                    {fleetVehicle.name || fleetVehicle.id}
                  </StyledFleetTypeNameText>
                </StyledFleetTypeNameLabel>
                <StyledFleetTypeSelect
                  onChange={(e) => onRowProfileChange(index, e)}
                  value={fleetVehicle.profile}
                  id={`input-fleet-type-profile-${index}`}
                >
                  {vehicleProfiles.map(({ name }) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </StyledFleetTypeSelect>
                <StyledFleetTypeAmountInput
                  key={`fleet-amount-${index}-${fleetVehicle.amount}`}
                  id={`input-fleet-amount-${index}`}
                  aria-label={isMobile ? vehiclesAmountLabelMobile : vehiclesAmountLabel}
                  type="number"
                  pattern="[0-9]*"
                  inputmode="numeric"
                  min="1"
                  max={maxValues.vehicles}
                  defaultValue={fleetVehicle.amount}
                  onBlur={(e) => onRowAmountBlur(index, e)}
                />
                <StyledFleetTypeUnitsLabel>{unitsLabel}</StyledFleetTypeUnitsLabel>
                <StyledFleetTypeActionIcon
                  id={`button-fleet-type-edit-${index}`}
                  iconUrl={iconEdit}
                  title={editFleetTypeTooltip}
                  onClick={() => onEditFleetType(index)}
                />
                <StyledFleetTypeActionIcon
                  id={`button-fleet-type-delete-${index}`}
                  variant="delete"
                  iconUrl={iconDelete}
                  title={deleteFleetTypeTooltip}
                  disabled={isLastFleetType}
                  onClick={() => !isLastFleetType && onDeleteFleetType(index)}
                />
              </StyledFleetTypeRow>
            );
          })}
          <StyledAddFleetTypeButton
            id="button-add-fleet-type"
            type="button"
            onClick={onAddFleetType}
          >
            {addFleetTypeLabel}
          </StyledAddFleetTypeButton>
          <>
            <Section>
              {!isProd && (
                <StyledDropText>
                  <div>{fleetTrans}</div>
                  <span onClick={onOpenClick}>{`${infoEnableSettings} ${
                    fleetEnabled ? `- ${fleetEnabledTrans}` : ''
                  } ${planEnabled ? `- ${planEnabledTrans}` : ''}`}</span>
                </StyledDropText>
              )}
            </Section>
            <Section>
              <Section>
                <Label>{timeLabelTrans}</Label>
                {showDriverSchedule && (
                  <Selector
                    id="button-time-window"
                    initialValue={timeTourParamKey}
                    options={timeOptions}
                    onChange={onTimeChange}
                  />
                )}
              </Section>
              <TimeRangeSlider
                id="slider-time-window"
                onChange={onSliderChange}
                timeAggregation={3}
                times={times}
              />
            </Section>
          </>
          {fleetManagerConfig && (
            <FleetManagerModal
              profiles={vehicleProfiles}
              types={vehicles}
              initialMode={fleetManagerConfig.mode}
              initialTypeIndex={fleetManagerConfig.typeIndex}
              oAuth={oAuth}
              apiKey={user.apiKey}
              canFleetTypeBeElectric={canFleetTypeBeElectric}
              onClose={onCloseFleetManager}
              onSaveProfile={onSaveProfile}
              onSaveType={onSaveType}
              onToggleType={onToggleType}
              onToggleAllTypes={onToggleAllTypes}
              onDeleteProfile={onDeleteProfile}
              onDeleteType={onDeleteFleetType}
            />
          )}
        </>
      )}
    </StyledWizardContent>
  );
};

export default withTranslation(Step1ContainerMultiFleet);
